import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Check } from "lucide-react";
import { Section, fadeUp } from "../components/sections";
import SeoHead from "../components/SeoHead";
import { INDUSTRIES, apiClient } from "../lib/site";

export default function IndustryDetailPage() {
  const { id } = useParams();
  const item = INDUSTRIES.find((i) => i.id === id);
  const [relatedCase, setRelatedCase] = useState(null);

  useEffect(() => {
    if (item?.related_case) {
      apiClient
        .get(`/case-studies/${item.related_case}`)
        .then((r) => setRelatedCase(r.data))
        .catch(() => setRelatedCase(null));
    } else {
      setRelatedCase(null);
    }
  }, [item]);

  if (!item) {
    return (
      <Section testId="industry-not-found">
        <SeoHead title="Industry not found" noindex />
        <p className="overline mb-4">Not found</p>
        <h1 className="font-serif-display text-5xl text-white font-light mb-8">Industry not found.</h1>
        <Link to="/industries" className="btn-secondary">Back to all industries</Link>
      </Section>
    );
  }

  return (
    <article data-testid="industry-detail-page">
      <SeoHead
        title={`${item.name} Software Development`}
        description={`${item.headline} — ${item.description}`}
        path={`/industries/${item.id}`}
        image={item.image}
      />

      {/* Hero */}
      <section className="relative px-6 md:px-12 lg:px-16 pt-24 md:pt-32 pb-16 border-b border-white/5">
        <Link
          to="/industries"
          className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition text-sm mb-12"
          data-testid="industry-back-link"
        >
          <ArrowLeft size={16} strokeWidth={1.5} /> All industries
        </Link>
        <motion.p {...fadeUp} className="overline mb-6">
          Industry · {item.name}
        </motion.p>
        <motion.h1
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.05 }}
          className="font-serif-display font-light text-white leading-[1.05] tracking-tight max-w-5xl"
          style={{ fontSize: "clamp(36px, 6vw, 80px)", letterSpacing: "-0.025em" }}
        >
          {item.headline}
        </motion.h1>
        <motion.p
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.1 }}
          className="mt-8 text-zinc-300 text-lg md:text-xl max-w-3xl leading-relaxed"
        >
          {item.description}
        </motion.p>
      </section>

      {/* Cover */}
      <section className="px-6 md:px-12 lg:px-16 py-16">
        <div className="aspect-[16/9] overflow-hidden bg-[#0A0A0C]">
          <img src={item.image} alt={item.name} className="w-full h-full object-cover opacity-90" />
        </div>
      </section>

      {/* Capabilities + meta */}
      <section className="px-6 md:px-12 lg:px-16 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <aside className="lg:col-span-4 space-y-10">
            <div>
              <p className="overline mb-4">Regulatory landscape</p>
              <p className="text-zinc-300 text-base leading-relaxed">{item.regulators}</p>
              <p className="mt-4 text-zinc-500 text-sm leading-relaxed">
                Every engagement is staffed with practitioners who have shipped production software
                inside the relevant regulatory perimeter.
              </p>
            </div>
            <div>
              <p className="overline mb-4">How we engage</p>
              <ul className="space-y-2 text-zinc-400 text-sm leading-relaxed">
                <li>· Discovery &amp; architecture diligence</li>
                <li>· Greenfield platform delivery</li>
                <li>· Modernisation &amp; migration</li>
                <li>· Embedded engineering pods</li>
                <li>· AI &amp; data engineering</li>
              </ul>
            </div>
          </aside>

          <div className="lg:col-span-8">
            <p className="overline mb-4">Capabilities in this vertical</p>
            <ul className="space-y-3">
              {item.capabilities.map((c, idx) => (
                <li key={idx} className="flex items-start gap-3 text-white text-base leading-relaxed">
                  <Check size={16} strokeWidth={1.5} style={{ color: "#C2533B" }} className="mt-1.5 shrink-0" />
                  {c}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Related case study */}
      {relatedCase && (
        <section className="border-t border-white/5 px-6 md:px-12 lg:px-16 py-24 bg-[#0A0A0C]">
          <p className="overline mb-6">Selected work in {item.name}</p>
          <Link
            to={`/case-studies/${relatedCase.slug}`}
            className="grid grid-cols-1 md:grid-cols-12 gap-10 group"
            data-testid="industry-related-case"
          >
            <div className="md:col-span-5">
              <div className="aspect-[4/3] overflow-hidden bg-[#050505]">
                <img
                  src={relatedCase.cover_image}
                  alt={relatedCase.title}
                  className="w-full h-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-105"
                />
              </div>
            </div>
            <div className="md:col-span-7 flex flex-col justify-center">
              <p className="overline mb-3" style={{ color: "#71717A" }}>
                {relatedCase.client} · {relatedCase.duration}
              </p>
              <h3 className="font-serif-display text-3xl md:text-4xl text-white font-light leading-snug group-hover:text-zinc-200 transition">
                {relatedCase.title}
              </h3>
              <p className="mt-5 text-zinc-400 text-base leading-relaxed max-w-2xl">
                {relatedCase.summary}
              </p>
              <p className="mt-8 inline-flex items-center gap-2 text-white text-sm tracking-wide">
                Read case study{" "}
                <ArrowUpRight size={14} strokeWidth={1.5} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </p>
            </div>
          </Link>
        </section>
      )}

      {/* CTA */}
      <section className="border-t border-white/5 px-6 md:px-12 lg:px-16 py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-8">
            <h2
              className="font-serif-display font-light text-white leading-[1.05] tracking-tight"
              style={{ fontSize: "clamp(32px, 5vw, 56px)", letterSpacing: "-0.025em" }}
            >
              Have a {item.name} platform on your roadmap?
            </h2>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link to="/contact" className="btn-primary" data-testid="industry-cta">
              Talk to a Strategist <ArrowUpRight size={14} strokeWidth={1.8} />
            </Link>
          </div>
        </div>
      </section>
    </article>
  );
}
